/**
 * Solana-specific chain service helpers.
 *
 * @remarks
 * Extracted from ChainService to keep the main class focused on chain registry.
 * Public methods remain on ChainService and delegate here.
 *
 * @internal
 */
import { normalizeChainID } from "../../shared/caip2";
import { SolanaRPCError } from "../../shared/error";
import { solanaMainnetChainID, solanaTestnetChainID } from "../../shared/vault-addresses";
/**
 * Maps a Solana chain ID to a friendly network identifier.
 * Delegates validation and normalisation to `shared/caip2.ts`.
 *
 * @see {@link https://github.com/ChainAgnostic/namespaces/blob/main/solana/caip2.md}
 */
export function _getSolanaNetwork(chainId) {
    const normalized = normalizeChainID(chainId, "solana");
    if (normalized === solanaMainnetChainID)
        return "solana:mainnet";
    if (normalized === solanaTestnetChainID)
        return "solana:testnet";
    throw new Error(`Unsupported Solana chain ID: "${chainId}" (normalized: "${normalized}"). Use "solana:mainnet", "solana:testnet", or equivalent CAIP-2 format.`);
}
/** Validates a Solana address format (Base58, 32–44 chars). */
export function _isValidSolanaAddress(address) {
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
    return base58Regex.test(address);
}
/** Returns the Blockdaemon RPC endpoint URL for the specified Solana network. */
export function _getSolanaRpcUrl(chainId, rpcBasePath) {
    const network = _getSolanaNetwork(chainId);
    const networkPath = network === "solana:mainnet" ? "mainnet" : "testnet";
    return `${rpcBasePath}/solana/${networkPath}/native`;
}
/**
 * Makes a JSON-RPC 2.0 call to a Solana RPC endpoint.
 *
 * @internal
 */
export async function _callSolanaRpc(chainId, method, params, rpcBasePath, bdApiKey, logger) {
    const rpcUrl = _getSolanaRpcUrl(chainId, rpcBasePath);
    const headers = {
        "Content-Type": "application/json",
    };
    if (bdApiKey) {
        headers["X-API-Key"] = bdApiKey;
    }
    const requestBody = {
        jsonrpc: "2.0",
        id: 1,
        method,
        params,
    };
    logger.debug("Making Solana RPC request", { rpcUrl, method, params });
    const response = await fetch(rpcUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
        throw SolanaRPCError.fromHttpError(response.status, response.statusText, {
            method,
            chainId: chainId,
        });
    }
    const jsonResponse = (await response.json());
    if (jsonResponse.error) {
        throw SolanaRPCError.fromRpcResponse(jsonResponse.error, {
            method,
            chainId: chainId,
        });
    }
    return jsonResponse.result;
}
/**
 * Calls the Solana getVoteAccounts RPC method for a specific validator.
 *
 * @internal
 */
export async function _getSolanaVoteAccounts(chainId, votePubkey, options, rpcBasePath, bdApiKey, logger) {
    const params = new SolanaRpcParamsBuilder()
        .withVotePubkey(votePubkey)
        .withOptions(options, logger)
        .build();
    return _callSolanaRpc(chainId, "getVoteAccounts", params, rpcBasePath, bdApiKey, logger);
}
/**
 * Builder for Solana RPC parameters.
 *
 * @remarks
 * Provides a fluent interface for building RPC parameters with proper
 * type conversion and validation.
 */
class SolanaRpcParamsBuilder {
    config = {};
    withVotePubkey(votePubkey) {
        this.config.votePubkey = votePubkey;
        return this;
    }
    withOptions(options, logger) {
        if (!options)
            return this;
        if (options.commitment !== undefined) {
            this.config.commitment = options.commitment;
        }
        if (options.keepUnstakedDelinquents !== undefined) {
            this.config.keepUnstakedDelinquents = options.keepUnstakedDelinquents;
        }
        if (options.delinquentSlotDistance !== undefined) {
            this.config.delinquentSlotDistance = options.delinquentSlotDistance;
        }
        const knownKeys = ["commitment", "keepUnstakedDelinquents", "delinquentSlotDistance"];
        for (const key of Object.keys(options)) {
            if (!knownKeys.includes(key)) {
                logger?.warn(`Unknown parameter '${key}' for getVoteAccounts, ignoring`);
            }
        }
        return this;
    }
    build() {
        return Object.keys(this.config).length > 0 ? [this.config] : [];
    }
}
const SOLANA_CONFIRMATION_POLL_INTERVAL_MS = 1000;
const SOLANA_CONFIRMATION_DEFAULT_TIMEOUT_MS = 90000;
/**
 * Polls JSON-RPC over HTTP until the signature reaches the target commitment
 * or the timeout expires.
 *
 * This uses a time-based timeout for convenience.
 * Solana's recommended robust approach is blockheight-based expiry tracking with
 * `lastValidBlockHeight` from `getLatestBlockhash`, which guarantees the transaction
 * either lands or expires deterministically on-chain.
 *
 * Notes:
 * - Uses `getSignatureStatuses`, per Solana RPC guidance.
 * - Does not call the deprecated `confirmTransaction` RPC method.
 * - Treats `confirmations === null` as rooted/max confirmations per RPC semantics.
 * - Sleep intervals are deadline-aware; a slow RPC response may exceed the timeout.
 *
 * @internal
 */
export async function _confirmSolanaTransaction(chainId, signature, rpcBasePath, bdApiKey, logger, targetCommitment = "finalized", timeout = SOLANA_CONFIRMATION_DEFAULT_TIMEOUT_MS, pollInterval = SOLANA_CONFIRMATION_POLL_INTERVAL_MS) {
    const transactionSignature = signature.trim();
    if (!transactionSignature) {
        throw new Error("Solana transaction signature is required.");
    }
    const deadline = Date.now() + timeout;
    for (;;) {
        const remainingTime = deadline - Date.now();
        if (remainingTime <= 0) {
            throw new Error(`Transaction confirmation timed out after ${timeout}ms. Signature: ${transactionSignature}`);
        }
        const statusResult = (await _callSolanaRpc(chainId, "getSignatureStatuses", [[transactionSignature], { searchTransactionHistory: true }], rpcBasePath, bdApiKey, logger));
        const status = statusResult.value[0];
        if (status) {
            if (status.err) {
                throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
            }
            // `confirmations: null` means rooted / max confirmations (see getSignatureStatuses docs).
            if (status.confirmations === null) {
                return;
            }
            const current = status.confirmationStatus;
            if ((targetCommitment === "finalized" && current === "finalized") ||
                (targetCommitment === "confirmed" && (current === "confirmed" || current === "finalized"))) {
                return;
            }
        }
        const timeUntilDeadline = deadline - Date.now();
        if (timeUntilDeadline <= 0) {
            throw new Error(`Transaction confirmation timed out after ${timeout}ms. Signature: ${transactionSignature}`);
        }
        const sleepDuration = Math.min(pollInterval, timeUntilDeadline);
        await new Promise((resolve) => setTimeout(resolve, sleepDuration));
    }
}
//# sourceMappingURL=data:application/json;base64,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